import { useState } from "react";
import { toast } from "sonner";
import { Share2, Download, Copy, Check, MessageCircle, Facebook, Twitter } from "lucide-react";
import { dataUrlToFile } from "@/lib/idCard";

const Btn = ({ testId, icon: Icon, label, onClick, className = "" }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`flex flex-col items-center gap-1.5 rounded-xl border border-talanta-green/10 bg-white py-3 text-xs font-semibold text-talanta-charcoal hover:bg-talanta-cream transition-colors ${className}`}
  >
    <Icon className="h-5 w-5" />
    {label}
  </button>
);

export const ShareButtons = ({ dataUrl, fileName, text, url, title, idPrefix = "share" }) => {
  const [copied, setCopied] = useState(false);
  const encoded = encodeURIComponent(`${text} ${url}`);
  const openUrl = (u) => window.open(u, "_blank", "noopener,noreferrer");

  const download = () => {
    const a = Object.assign(document.createElement("a"), { href: dataUrl, download: fileName });
    a.click();
    toast.success("Saved to your device");
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = async () => {
    if (!navigator.share) return download();
    try {
      const file = await dataUrlToFile(dataUrl, fileName);
      const payload = navigator.canShare?.({ files: [file] }) ? { files: [file], title, text: `${text} ${url}` } : { title, text, url };
      await navigator.share(payload);
    } catch (e) {
      if (e?.name !== "AbortError") download();
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2">
        <Btn testId={`${idPrefix}-whatsapp-btn`} icon={MessageCircle} label="WhatsApp" onClick={() => openUrl(`https://wa.me/?text=${encoded}`)} className="text-emerald-700" />
        <Btn testId={`${idPrefix}-x-btn`} icon={Twitter} label="X" onClick={() => openUrl(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`)} />
        <Btn testId={`${idPrefix}-facebook-btn`} icon={Facebook} label="Facebook" onClick={() => openUrl(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)} className="text-blue-700" />
        <Btn testId={`${idPrefix}-copy-link-btn`} icon={copied ? Check : Copy} label={copied ? "Copied" : "Copy link"} onClick={copyLink} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid={`${idPrefix}-download-btn`}
          onClick={download}
          disabled={!dataUrl}
          className="rounded-xl border border-talanta-green/20 bg-white py-3 text-sm font-semibold text-talanta-green hover:bg-talanta-cream flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> Save image
        </button>
        <button
          data-testid={`${idPrefix}-native-btn`}
          onClick={nativeShare}
          disabled={!dataUrl}
          className="rounded-xl bg-talanta-red hover:bg-talanta-red/90 py-3 text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Share2 className="h-4 w-4" /> Share card
        </button>
      </div>
    </div>
  );
};
